import { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Button, Input, Radio } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

const stopKeyPropagation = (event) => event.stopPropagation();
const ALL_OPTION = "__all__";

export function ColumnSearchDropdown({ selectedKeys, setSelectedKeys, visible, placeholder, onApply, onClear }) {
    const { t } = useTranslation("table");
    const inputRef = useRef(null);

    useEffect(() => {
        if (visible) {
            const timer = setTimeout(() => inputRef.current?.select(), 50);
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [visible]);

    const apply = () => onApply((selectedKeys?.[0] ?? "").toString().trim());

    return (
        <div className="flex w-64 flex-col gap-2 p-2" onKeyDown={stopKeyPropagation}>
            <Input
                ref={inputRef}
                allowClear
                prefix={<SearchOutlined className="text-gray-400" />}
                placeholder={placeholder || t("searchPlaceholder")}
                value={selectedKeys?.[0]}
                onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                onPressEnter={apply}
            />
            <div className="flex items-center justify-between">
                <Button size="small" type="link" className="!px-0" onClick={onClear}>
                    {t("reset")}
                </Button>
                <Button size="small" type="primary" onClick={apply}>
                    {t("apply")}
                </Button>
            </div>
        </div>
    );
}

ColumnSearchDropdown.propTypes = {
    selectedKeys: PropTypes.array,
    setSelectedKeys: PropTypes.func.isRequired,
    visible: PropTypes.bool,
    placeholder: PropTypes.string,
    onApply: PropTypes.func.isRequired,
    onClear: PropTypes.func.isRequired,
};

export function ColumnSelectDropdown({ selectedKeys, setSelectedKeys, options, onApply, onClear }) {
    const { t } = useTranslation("table");
    const selected = selectedKeys?.[0];

    return (
        <div className="flex min-w-48 flex-col gap-2 p-2" onKeyDown={stopKeyPropagation}>
            <Radio.Group
                className="flex flex-col gap-1.5 px-1 py-1"
                value={selected ?? ALL_OPTION}
                onChange={(e) => setSelectedKeys(e.target.value === ALL_OPTION ? [] : [e.target.value])}>
                <Radio value={ALL_OPTION}>{t("all")}</Radio>
                {options.map((option) => (
                    <Radio key={String(option.value)} value={option.value}>
                        {option.label}
                    </Radio>
                ))}
            </Radio.Group>
            <div className="flex items-center justify-between border-t border-gray-100 pt-2">
                <Button size="small" type="link" className="!px-0" onClick={onClear}>
                    {t("reset")}
                </Button>
                <Button size="small" type="primary" onClick={() => onApply(selected)}>
                    {t("apply")}
                </Button>
            </div>
        </div>
    );
}

ColumnSelectDropdown.propTypes = {
    selectedKeys: PropTypes.array,
    setSelectedKeys: PropTypes.func.isRequired,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]).isRequired,
            label: PropTypes.node.isRequired,
        }),
    ).isRequired,
    onApply: PropTypes.func.isRequired,
    onClear: PropTypes.func.isRequired,
};
